import { useEffect, useState } from "react";
import { ANALYTICS_CONFIG } from "@/lib/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * SEOHead - Injects meta tags via direct DOM manipulation.
 * Avoids react-helmet-async to prevent conflicts with Emergent's babel plugin.
 */
export default function SEOHead({ pageSlug, fallbackTitle = "Upscale Nails & Spa", fallbackDescription = "" }) {
  const [seo, setSeo] = useState(null);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/seo/pages/${pageSlug}`)
      .then(r => r.json())
      .then(data => setSeo(data))
      .catch(() => {});
  }, [pageSlug]);

  useEffect(() => {
    if (!seo) return;
    const page = seo.page || {};
    const global = seo.global || {};
    const template = global.site_title_template || "{page}";

    const rawTitle = page.title || fallbackTitle;
    const fullTitle = template.replace("{page}", rawTitle);
    const desc = page.meta_description || global.default_meta_description || fallbackDescription;
    const ogTitle = page.og_title || rawTitle;
    const ogDesc = page.og_description || desc;
    const ogImage = page.og_image || global.default_og_image || "";
    const canonical = page.canonical_url || `${BACKEND_URL}/${pageSlug === "home" ? "" : pageSlug}`;
    const gscCode = global.google_search_console_code;
    const gaId = global.google_analytics_id;
    const fbPixel = global.facebook_pixel_id;

    // Set document title
    document.title = fullTitle;

    // Helper to set/create meta tags
    const setMeta = (attr, attrVal, content) => {
      let el = document.querySelector(`meta[${attr}="${attrVal}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, attrVal);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content || "");
    };

    // Helper to set/create link tags
    const setLink = (rel, href) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    // Standard meta tags
    setMeta("name", "description", desc);
    if (page.noindex) setMeta("name", "robots", "noindex,nofollow");
    setLink("canonical", canonical);

    // Open Graph
    setMeta("property", "og:title", ogTitle);
    setMeta("property", "og:description", ogDesc);
    setMeta("property", "og:type", "website");
    setMeta("property", "og:url", canonical);
    if (ogImage) setMeta("property", "og:image", ogImage);

    // Twitter Card
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", page.twitter_title || ogTitle);
    setMeta("name", "twitter:description", page.twitter_description || ogDesc);
    if (ogImage) setMeta("name", "twitter:image", ogImage);

    // Google Search Console
    if (gscCode) setMeta("name", "google-site-verification", gscCode);

    // Google Analytics — env (analytics.js) is the single source of truth for GA.
    // Only fall back to the DB-stored SEO GA id when no REACT_APP_GA4_MEASUREMENT_ID
    // is configured, so the two layers can never double-inject gtag.
    if (gaId && !ANALYTICS_CONFIG.ga4 && !document.querySelector(`script[src*="gtag/js?id=${gaId}"]`)) {
      const s1 = document.createElement("script");
      s1.async = true;
      s1.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
      document.head.appendChild(s1);
      const s2 = document.createElement("script");
      s2.textContent = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${gaId}');`;
      document.head.appendChild(s2);
    }

    // Facebook Pixel
    if (fbPixel && !window.fbq) {
      const s = document.createElement("script");
      s.textContent = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${fbPixel}');fbq('track','PageView');`;
      document.head.appendChild(s);
    }
  }, [seo, pageSlug, fallbackTitle, fallbackDescription]);

  return null; // No JSX — all meta set via DOM
}
